import { memo, useMemo } from "react";
import PropTypes from "prop-types";

function MicButton({
  state = "idle", // "idle" | "recording" | "paused" | "processing"
  onStart,
  className = "",
  processingLabel = "Processing…",
}) {
  const isIdle = state === "idle";
  const isPaused = state === "paused";
  const isRecording = state === "recording";
  const isProcessing = state === "processing";

  const glow1 = useMemo(() => ({
    background:
      "radial-gradient(55% 55% at 50% 45%, rgba(99,102,241,0.55), rgba(147,51,234,0.35) 60%, transparent 80%)",
  }), []);
  const glow2 = useMemo(() => ({
    background:
      "conic-gradient(from 90deg at 50% 50%, #6366f1, #22d3ee, #a78bfa, #6366f1)",
    animation: "blobSpin 8s linear infinite",
    opacity: 0.6,
    animationPlayState: isPaused ? "paused" : "running",
  }), [isPaused]);
  const glow3 = useMemo(() => ({
    background:
      "radial-gradient(50% 60% at 50% 50%, rgba(34,211,238,0.60), rgba(99,102,241,0.45))",
    animation: "blobMorph 5.8s ease-in-out infinite",
    mixBlendMode: "plus-lighter",
    animationPlayState: isPaused ? "paused" : "running",
  }), [isPaused]);
  const glow4 = useMemo(() => ({
    background:
      "radial-gradient(65% 65% at 50% 50%, rgba(30,64,175,0.75), rgba(99,102,241,0.55))",
    animation: "blobPulse 2.4s ease-in-out infinite",
    animationPlayState: isPaused ? "paused" : "running",
  }), [isPaused]);

  return (
    <div className={`flex flex-col items-center gap-4 select-none ${className}`} aria-busy={isProcessing ? "true" : "false"}>
      {isIdle ? (
        <>
          <button
            type="button"
            onClick={onStart}
            disabled={!onStart}
            aria-label="Start recording"
            className={[
              "h-28 w-28 rounded-full flex items-center justify-center bg-neutral-50 text-neutral-900 border border-neutral-200 shadow-md",
              "transition-transform duration-200",
              onStart ? "hover:scale-105 active:scale-95" : "cursor-not-allowed opacity-50",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-neutral-900/40",
            ].join(" ")}
          >
            <svg viewBox="0 0 24 24" className="h-16 w-16" aria-hidden="true">
              <rect x="6.5" y="9" width="2" height="6" rx="1" fill="currentColor" />
              <rect x="11" y="6" width="2" height="12" rx="1" fill="currentColor" />
              <rect x="15.5" y="9" width="2" height="6" rx="1" fill="currentColor" />
            </svg>
          </button>
          <p className="text-gray-500 font-normal text-lg">Start a new consultation</p>
        </>
      ) : (isRecording || isPaused) ? (
        <div className="relative h-40 w-40 rounded-full" role="status" aria-label={isPaused ? "Recording paused" : "Recording"}>
          <span className="absolute inset-0 rounded-full blur-2xl opacity-70" style={glow1} />
          <span className="absolute inset-0 rounded-[46%] blur-xl" style={glow2} />
          <span className="absolute inset-[10%] rounded-[44%] blur-md" style={glow3} />
          <span className="absolute inset-[22%] rounded-[42%] backdrop-blur-sm" style={glow4} />
          {isPaused && (
            <span className="absolute inset-0 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-10 h-10 text-white/90" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 6v12M14 6v12" strokeLinecap="round" />
              </svg>
            </span>
          )}
        </div>
      ) : (
        // PROCESSING (same blob look, muted & spinner, no lock)
        <div className="relative h-40 w-40 rounded-full" role="status" aria-label="Processing audio" aria-busy="true">
          {/* muted blobs */}
          <span className="absolute inset-0 rounded-full blur-2xl opacity-70 saturate-0" style={glow1} />
          <span className="absolute inset-0 rounded-[46%] blur-xl saturate-0 [animation-duration:12s]" style={glow2} />
          <span className="absolute inset-[10%] rounded-[44%] blur-md saturate-0 [animation-duration:8.5s]" style={glow3} />
          <span className="absolute inset-[22%] rounded-[42%] backdrop-blur-sm saturate-0 [animation-duration:3.4s]" style={glow4} />

          {/* spinner ring */}
          <div className="absolute -inset-1 rounded-full border-4 border-neutral-200" />
          <div className="absolute -inset-1 rounded-full border-4 border-transparent border-t-neutral-400 animate-spin [animation-duration:1.4s]" />

          {/* label */}
          <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-sm text-neutral-600">
            {processingLabel || "Processing…"}
          </span>
        </div>
      )}

      {/* status text for recording/paused */}
      {state !== "idle" && !isProcessing && (
        <div className="text-sm text-neutral-400">{isPaused ? "Paused" : "Recording…"}</div>
      )}

      <style>{`
        @keyframes blobSpin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }
        @keyframes blobMorph {
          0%{border-radius:46% 54% 48% 52% / 54% 46% 54% 46%}
          33%{border-radius:58% 42% 52% 48% / 42% 58% 42% 58%}
          66%{border-radius:48% 52% 58% 42% / 60% 40% 60% 40%}
          100%{border-radius:46% 54% 48% 52% / 54% 46% 54% 46%}
        }
        @keyframes blobPulse { 0%,100%{transform:scale(.96);opacity:.85} 50%{transform:scale(1.02);opacity:1} }
      `}</style>
    </div>
  );
}

MicButton.propTypes = {
  state: PropTypes.oneOf(["idle", "recording", "paused", "processing"]),
  onStart: PropTypes.func,
  className: PropTypes.string,
  processingLabel: PropTypes.string,
};

export default memo(MicButton);
